import * as React from 'react'

interface Props {
  page: number
  perPage: number
  total: number
  onSelect?: (page: number) => void
}

export default function Pagination({ page, perPage, total, onSelect }: Props): React.ReactElement {
  const totalPages = Math.ceil(total / perPage)
  let showPages: number[]

  if (page <= 2) {
    showPages = [1, 2, 3]
  } else if (page >= totalPages - 1) {
    showPages = [totalPages - 2, totalPages - 1, totalPages]
  } else {
    showPages = [page - 1, page, page + 1]
  }

  showPages = showPages.filter((showPage) => showPage >= 1 && showPage <= totalPages)

  if (!showPages.length) {
    showPages = [1]
  }

  return (
    <nav aria-label="Page navigation example">
      <ul className="flex h-8 items-center -space-x-px text-sm">
        <li>
          <button
            type="button"
            onClick={() => {
              if (onSelect) {
                onSelect(1)
              }
            }}
            className="button button-default-outline h-8 rounded-r-none border-e-0 leading-tight"
          >
            <span className="sr-only">First</span>
            <svg className="size-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m17 16-4-4 4-4m-6 8-4-4 4-4" />
            </svg>
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => {
              if (onSelect) {
                onSelect(page - 1 <= 1 ? 1 : page - 1)
              }
            }}
            className="button button-default-outline h-8 rounded-none border-e-0 leading-tight"
          >
            <span className="sr-only">Previous</span>
            <svg className="size-2.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 1 1 5l4 4" />
            </svg>
          </button>
        </li>
        {showPages.map((showPage) => {
          if (page === showPage) {
            return (
              <button
                type="button"
                key={`page_${showPage}`}
                className="button button-default-outline h-8 rounded-none border-e-0 bg-black/5 leading-tight dark:bg-white/15"
                onClick={() => {
                  if (onSelect) {
                    onSelect(showPage)
                  }
                }}
                aria-current="page"
                title={`Page ${showPage}`}
                aria-label={`Page ${showPage}`}
              >
                {showPage}
              </button>
            )
          }

          return (
            <button
              type="button"
              key={`page_${showPage}`}
              className="button button-default-outline h-8 rounded-none border-e-0 leading-tight"
              onClick={() => {
                if (onSelect) {
                  onSelect(showPage)
                }
              }}
              title={`Page ${showPage}`}
              aria-label={`Page ${showPage}`}
            >
              {showPage}
            </button>
          )
        })}
        <li>
          <button
            type="button"
            onClick={() => {
              if (onSelect) {
                onSelect(page + 1 >= totalPages ? totalPages : page + 1)
              }
            }}
            className="button button-default-outline h-8 rounded-none border-e-0 leading-tight"
          >
            <span className="sr-only">Next</span>
            <svg className="size-2.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4" />
            </svg>
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => {
              if (onSelect) {
                onSelect(totalPages)
              }
            }}
            className="button button-default-outline h-8 rounded-l-none leading-tight"
          >
            <span className="sr-only">Last</span>
            <svg className="size-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m7 16 4-4-4-4m6 8 4-4-4-4" />
            </svg>
          </button>
        </li>
      </ul>
    </nav>
  )
}
